<template>
    <div class="contact-us-edit-container">
      <el-card shadow="never">
        <template #header>
          <div class="card-header">
            <span>Edit Contact Information</span>
          </div>
        </template>
  
        <el-form
          ref="contactFormRef"
          :model="contactInfo"
          :rules="formRules"
          label-width="140px"
          label-position="right"
          v-loading="loading"
        >
          <!-- Address -->
          <el-divider content-position="left">Address</el-divider>
          <el-form-item label="Address Line 1" prop="address.line1">
            <el-input v-model="contactInfo.address.line1" placeholder="e.g., Quzhou Industrial Park, Handan City"></el-input>
          </el-form-item>
          <el-form-item label="Address Line 2" prop="address.line2">
            <el-input v-model="contactInfo.address.line2" placeholder="e.g., Hebei Province, China 057250"></el-input>
          </el-form-item>
  
          <!-- Email -->
          <el-divider content-position="left">Email</el-divider>
          <el-form-item label="Email 1" prop="email.email1">
            <el-input v-model="contactInfo.email.email1" placeholder="e.g., sales@langtaichem.com"></el-input>
          </el-form-item>
          <el-form-item label="Email 2" prop="email.email2">
            <el-input v-model="contactInfo.email.email2" placeholder="e.g., wang@langtaichem.com"></el-input>
          </el-form-item>
  
          <!-- Telephone -->
          <el-divider content-position="left">Telephone</el-divider>
          <el-form-item label="Telephone 1" prop="tel.tel1">
            <el-input v-model="contactInfo.tel.tel1" placeholder="e.g., 0086-310-8037811"></el-input>
          </el-form-item>
          <el-form-item label="Telephone 2" prop="tel.tel2">
            <el-input v-model="contactInfo.tel.tel2" placeholder="e.g., 0086-310-8922055"></el-input>
          </el-form-item>
  
          <!-- Social Contacts -->
          <el-divider content-position="left">Social & Messaging</el-divider>
           <el-form-item label="QQ Number" prop="qq.number">
            <el-input v-model="contactInfo.qq.number" placeholder="e.g., 674938357"></el-input>
          </el-form-item>
           <el-form-item label="WeChat ID" prop="wechat.id">
            <el-input v-model="contactInfo.wechat.id" placeholder="e.g., w4915333"></el-input>
          </el-form-item>
           <el-form-item label="WhatsApp Number" prop="whatsapp.display">
            <el-input v-model="contactInfo.whatsapp.display" placeholder="e.g., +86 18232060996"></el-input>
             <small style="color: #999;">(This is the text displayed)</small>
          </el-form-item>
          <el-form-item label="WhatsApp Link" prop="whatsapp.link">
             <el-input v-model="contactInfo.whatsapp.link" placeholder="e.g., https://wa.me/8618232030996"></el-input>
              <small style="color: #999;">(Full link including https://wa.me/)</small>
          </el-form-item>
  
          <!-- Actions -->
          <el-form-item>
            <el-button type="primary" @click="saveContactInfo" :loading="saving">Save Changes</el-button>
            <el-button @click="resetForm">Reset</el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, onMounted } from 'vue';
  import { ElMessage, ElMessageBox } from 'element-plus'; // Import message and potentially confirm box
  
  // --- State ---
  const contactFormRef = ref(null); // Reference to the form component
  const loading = ref(false);      // Loading state for fetching data
  const saving = ref(false);       // Saving state for update operation
  const initialData = ref(null);   // Store initially loaded data for reset
  
  // Use reactive for the nested object structure
  const contactInfo = reactive({
    address: { line1: '', line2: '' },
    email: { email1: '', email2: '' },
    tel: { tel1: '', tel2: '' },
    qq: { number: '' },
    wechat: { id: '' },
    whatsapp: { display: '', link: '' } // Store display text and link separately
  });
  
  // --- Validation Rules (Example) ---
  const formRules = reactive({
    'address.line1': [
      { required: true, message: 'Please enter Address Line 1', trigger: 'blur' }
    ],
    'email.email1': [
      { required: true, message: 'Please enter Email 1', trigger: 'blur' },
      { type: 'email', message: 'Please enter a valid email address', trigger: ['blur', 'change'] }
    ],
    'tel.tel1': [
      { required: true, message: 'Please enter Telephone 1', trigger: 'blur' }
    ],
     'whatsapp.link': [
      { type: 'url', message: 'Please enter a valid URL', trigger: ['blur', 'change'] }
    ],
    // Add more rules as needed
  });
  
  // --- API Interaction (Replace with your actual API logic) ---
  async function fetchContactData() {
    loading.value = true;
    console.log('Fetching contact data...');
    try {
      // ** Replace with your actual API call **
      // Example using fetch:
      const response = await fetch('/api/contactInfo/get'); // Replace with your GET endpoint
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
  
      // Use Object.assign for deep assignment with reactive object
      Object.assign(contactInfo.address, data.address || {});
      Object.assign(contactInfo.email, data.email || {});
      Object.assign(contactInfo.tel, data.tel || {});
      Object.assign(contactInfo.qq, data.qq || {});
      Object.assign(contactInfo.wechat, data.wechat || {});
      Object.assign(contactInfo.whatsapp, data.whatsapp || {});
  
      // Store initial data for reset functionality
      initialData.value = JSON.parse(JSON.stringify(contactInfo)); // Deep copy
  
      console.log('Data fetched:', contactInfo);
      ElMessage.success('Contact data loaded.');
  
    } catch (error) {
      console.error('Failed to fetch contact data:', error);
      ElMessage.error('Failed to load contact data. Please try again.');
       // Set default values or leave empty on error?
      // initialData.value = JSON.parse(JSON.stringify(contactInfo)); // Store empty state for reset
    } finally {
      loading.value = false;
    }
  }
  
  async function updateContactData() {
    saving.value = true;
    console.log('Updating contact data:', contactInfo);
    try {
      // ** Replace with your actual API call **
      // Example using fetch:
      const response = await fetch('/api/contactInfo/update', { // Replace with your POST/PUT endpoint
        method: 'POST', // or 'PUT'
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactInfo),
      });
  
      if (!response.ok) {
        // Try to get error details from response body if possible
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || JSON.stringify(errorData);
        } catch(e) { /* ignore json parsing error */ }
        throw new Error(errorMsg);
      }
  
      const result = await response.json(); // Or handle based on API response
  
      // Update initial data after successful save if reset should reflect saved state
      initialData.value = JSON.parse(JSON.stringify(contactInfo));
  
      ElMessage.success('Contact information updated successfully!');
      console.log('Update successful:', result);
  
    } catch (error) {
      console.error('Failed to update contact data:', error);
      ElMessage.error(`Failed to update contact data: ${error.message}`);
    } finally {
      saving.value = false;
    }
  }
  
  // --- Methods ---
  const saveContactInfo = () => {
    if (!contactFormRef.value) return;
    contactFormRef.value.validate((valid) => {
      if (valid) {
        console.log('Form is valid, attempting to save...');
        // Optional: Confirmation dialog
        // ElMessageBox.confirm('Are you sure you want to save the changes?', 'Confirm Save', {
        //   confirmButtonText: 'Save',
        //   cancelButtonText: 'Cancel',
        //   type: 'warning',
        // }).then(() => {
             updateContactData();
        // }).catch(() => {
        //   ElMessage.info('Save cancelled');
        // });
      } else {
        console.log('Form validation failed!');
        ElMessage.error('Please correct the errors in the form.');
        return false;
      }
    });
  };
  
  const resetForm = () => {
    if (!contactFormRef.value) return;
    if (initialData.value) {
       // Use Object.assign to update the reactive object deeply from the stored initial data
      Object.assign(contactInfo.address, initialData.value.address);
      Object.assign(contactInfo.email, initialData.value.email);
      Object.assign(contactInfo.tel, initialData.value.tel);
      Object.assign(contactInfo.qq, initialData.value.qq);
      Object.assign(contactInfo.wechat, initialData.value.wechat);
      Object.assign(contactInfo.whatsapp, initialData.value.whatsapp);
  
      // Clear validation states after reset
      contactFormRef.value.clearValidate();
      ElMessage.info('Form reset to last loaded state.');
    } else {
        ElMessage.warning('No initial data loaded to reset to.');
        // Or manually clear the form if needed
        // contactFormRef.value.resetFields(); // This might not work well with nested reactive objects
    }
  };
  
  // --- Lifecycle Hook ---
  onMounted(() => {
    fetchContactData();
  });
  
  </script>
  
  <style scoped>
  .contact-us-edit-container {
    padding: 20px;
    max-width: 800px; /* Adjust as needed */
    margin: 20px auto;
  }
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: bold;
  }
  
  .el-form-item {
    margin-bottom: 22px; /* Consistent spacing */
  }
  
  .el-divider {
    margin-top: 10px;
    margin-bottom: 30px;
  }
  </style>